"use client"

import { useState, useEffect, useContext, useMemo } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  TextField,
  Typography,
  Grid,
  Divider,
  Box,
  Autocomplete,
} from "@mui/material"
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  VpnKey as VpnKeyIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"

export default function UserForm({
  mode = "create", // 'create' | 'edit'
  initialValues = {},
  onSubmit,
  onSuccess,
  onError,
  renderActions, // (helpers) => ReactNode
  formId,
}) {
  const { getRolUsuario } = useContext(ErpContext)

  const [values, setValues] = useState({
    usuario: "",
    nombre: "",
    apellido: "",
    telefono: "",
    email: "",
    direccion: "",
    localidad: "",
    provincia: "",
    password: "",
    dniCuitCuil: "",
    id: undefined, // idUsuario para edición
  })

  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [rolesUsuarios, setRolesUsuarios] = useState([])
  const [selectedRol, setSelectedRol] = useState(null)

  const isCreate = mode === "create"

  useEffect(() => {
    // Cargar roles
    const fetchRoles = async () => {
      try {
        const roles = await getRolUsuario()
        setRolesUsuarios(roles || [])
      } catch (e) {
        // noop
      }
    }
    fetchRoles()
  }, [getRolUsuario])

  useEffect(() => {
    // Solo setear initial values si realmente hay valores (modo edición)
    if (!initialValues || Object.keys(initialValues).length === 0) {
      // Modo crear: mantener valores vacíos
      return
    }
    
    // Modo editar: cargar valores del usuario
    const next = {
      usuario: initialValues.usuario || "",
      nombre: initialValues.nombre || "",
      apellido: initialValues.apellido || "",
      telefono: initialValues.telefono || "",
      email: initialValues.email || "",
      direccion: initialValues.direccion || "",
      localidad: initialValues.localidad || "",
      provincia: initialValues.provincia || "",
      password: "", // en edición, no precargar
      dniCuitCuil: initialValues.dniCuitCuil || "",
      id: initialValues.id || initialValues.idUsuario,
    }
    setValues(next)
  }, [initialValues])

  // Mapear rol inicial cuando roles estén disponibles (solo en modo edición)
  useEffect(() => {
    if (rolesUsuarios.length === 0) return
    
    // Si es modo crear, no hacer nada (el usuario seleccionará manualmente)
    if (isCreate) {
      return
    }
    
    // Si es modo editar y hay un rol inicial
    if (initialValues && (initialValues.rol !== undefined && initialValues.rol !== null)) {
      const rolId = typeof initialValues.rol === "object" ? initialValues.rol.id : initialValues.rol
      const match = rolesUsuarios.find((r) => r.id === rolId)
      if (match) setSelectedRol({ label: match.descripcion, id: match.id })
    }
  }, [rolesUsuarios, initialValues, isCreate])

  const roleOptions = useMemo(
    () => (rolesUsuarios || []).map((it) => ({ label: `${it.descripcion}`, id: it.id })),
    [rolesUsuarios],
  )

  const validate = () => {
    const newErrors = {}
    if (!values.usuario.trim()) newErrors.usuario = "El usuario es requerido"
    if (isCreate && !values.password.trim()) newErrors.password = "La contraseña es requerida"
    if (!selectedRol) newErrors.rol = "Debe seleccionar un rol"
    if (values.email && !/\S+@\S+\.\S+/.test(values.email)) newErrors.email = "Email inválido"
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }))
  }

  const resetForm = () => {
    setValues({
      usuario: "",
      nombre: "",
      apellido: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      password: "",
      dniCuitCuil: "",
      id: initialValues.id || initialValues.idUsuario,
    })
    setSelectedRol(null)
    setErrors({})
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload = { ...values, rol: selectedRol }
      await onSubmit?.(payload)
      onSuccess?.()
      if (isCreate) resetForm()
    } catch (err) {
      onError?.(err)
    } finally {
      setSubmitting(false)
    }
  }

  const toggleShowPassword = () => setShowPassword((p) => !p)

  return (
    <form onSubmit={handleSubmit} id={formId} style={{ width: '100%' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Usuario"
            variant="outlined"
            size="small"
            fullWidth
            value={values.usuario}
            onChange={handleChange("usuario")}
            error={!!errors.usuario}
            helperText={errors.usuario}
            autoComplete="off"
            InputProps={{
              startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Contraseña"
            variant="outlined"
            size="small"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={values.password}
            onChange={handleChange("password")}
            error={!!errors.password}
            helperText={errors.password}
            autoComplete="new-password"
            InputProps={{
              startAdornment: <VpnKeyIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
            required={isCreate}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            size="small"
            disablePortal
            options={roleOptions}
            value={selectedRol}
            fullWidth
            onChange={(event, newValue) => setSelectedRol(newValue || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rol *"
                variant="outlined"
                error={!!errors.rol}
                helperText={errors.rol}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SupervisorAccountIcon sx={{ color: "#5b5b5f", mr: 1, ml: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: "#5b5b5f" },
                    "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 0.5 }}>
            Información Personal
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Nombre"
            variant="outlined"
            size="small"
            fullWidth
            value={values.nombre}
            onChange={handleChange("nombre")}
            InputProps={{ startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Apellido"
            variant="outlined"
            size="small"
            fullWidth
            value={values.apellido}
            onChange={handleChange("apellido")}
            InputProps={{ startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="DNI/CUIT/CUIL"
            variant="outlined"
            size="small"
            fullWidth
            value={values.dniCuitCuil}
            onChange={handleChange("dniCuitCuil")}
            InputProps={{ startAdornment: <BadgeIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 0.5 }}>
            Información de Contacto
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Teléfono"
            variant="outlined"
            size="small"
            fullWidth
            value={values.telefono}
            onChange={handleChange("telefono")}
            InputProps={{ startAdornment: <PhoneIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            variant="outlined"
            size="small"
            type="email"
            fullWidth
            value={values.email}
            onChange={handleChange("email")}
            error={!!errors.email}
            helperText={errors.email}
            InputProps={{ startAdornment: <EmailIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 0.5 }}>
            Información de Ubicación
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Dirección"
            variant="outlined"
            size="small"
            fullWidth
            value={values.direccion}
            onChange={handleChange("direccion")}
            InputProps={{ startAdornment: <LocationIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Localidad"
            variant="outlined"
            size="small"
            fullWidth
            value={values.localidad}
            onChange={handleChange("localidad")}
            InputProps={{ startAdornment: <LocationIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Provincia"
            variant="outlined"
            size="small"
            fullWidth
            value={values.provincia}
            onChange={handleChange("provincia")}
            InputProps={{ startAdornment: <LocationIcon sx={{ color: "#5b5b5f", mr: 1 }} /> }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#5b5b5f" },
                "&.Mui-focused fieldset": { borderColor: "#5b5b5f" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5b5b5f" },
            }}
          />
        </Grid>

        {typeof renderActions === "function" && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
              {renderActions({ resetForm, submitting })}
            </Box>
          </Grid>
        )}
      </Grid>
    </form>
  )
}


